import { useMemo, useState } from 'react'
import {
  AlertTriangle, CalendarClock, Clock, Download, FileText, History,
  Loader2, Mail, Pencil, Plus, Settings2, Trash2, Upload, X,
} from 'lucide-react'
import { toast } from 'sonner'
import { PageHeader } from '@/components/layout/page-header'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { fristen as fristenApi } from '@/utils/api'
import {
  FristCategory, FristDocument, useCreateFristCategory, useDeleteFristCategory,
  useFristenCategories, useFristenHistory, useFristenSettings, useFristenStats,
  useMarkFristRequested, useRenameFristCategory, useSendFristEmail,
  useUpdateFristenSettings, useUploadFristDocument,
} from '@/hooks/useFristen'
import { sendFristEmailSchema } from '@/schemas/fristen'

const COLOR_BADGES: Record<string, string> = {
  green: 'bg-green-100 text-green-800 border-green-200',
  yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  orange: 'bg-orange-100 text-orange-800 border-orange-200',
  red: 'bg-red-100 text-red-800 border-red-200',
}

const ROW_BG: Record<string, string> = {
  yellow: 'bg-yellow-50/60',
  orange: 'bg-orange-50/60',
  red: 'bg-red-50/60',
}

const formatGermanDate = (iso: string | null) => {
  if (!iso) return '-'
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function expiryLabel(doc: FristDocument) {
  if (doc.daysLeft < 0) return `seit ${-doc.daysLeft} Tag${doc.daysLeft === -1 ? '' : 'en'} abgelaufen`
  if (doc.daysLeft === 0) return 'läuft heute ab'
  return `noch ${doc.daysLeft} Tag${doc.daysLeft === 1 ? '' : 'e'}`
}

function StatusBadge({ doc }: { doc: FristDocument }) {
  if (doc.snoozed) {
    return (
      <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200 gap-1">
        <Clock className="h-3 w-3" />
        Angefragt {doc.snoozeUntil ? `(bis ${formatGermanDate(doc.snoozeUntil)})` : ''}
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className={cn('gap-1', COLOR_BADGES[doc.color])}>
      {doc.stageRank >= 3 && <AlertTriangle className="h-3 w-3" />}
      {expiryLabel(doc)}
    </Badge>
  )
}

export default function Fristen() {
  const { data: groups, isLoading } = useFristenCategories()
  const { data: stats } = useFristenStats()

  const [selected, setSelected] = useState<Set<number>>(new Set())

  // Dialog-States
  const [uploadTarget, setUploadTarget] = useState<FristCategory | null>(null)
  const [historyTarget, setHistoryTarget] = useState<FristCategory | null>(null)
  const [renameTarget, setRenameTarget] = useState<FristCategory | null>(null)
  const [newCategoryGroup, setNewCategoryGroup] = useState<string | null>(null)
  const [emailOpen, setEmailOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [zipLoading, setZipLoading] = useState(false)

  const allDocs = useMemo(() => {
    const map = new Map<number, { doc: FristDocument; categoryName: string }>()
    groups?.forEach(g => g.categories.forEach(c => {
      if (c.document) map.set(c.document.id, { doc: c.document, categoryName: c.name })
    }))
    return map
  }, [groups])

  const toggleSelected = (docId: number) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(docId)) next.delete(docId)
      else next.add(docId)
      return next
    })
  }

  const handleZipDownload = async () => {
    if (selected.size === 0) return
    setZipLoading(true)
    try {
      const res = await fristenApi.downloadZip([...selected])
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }))
      const a = document.createElement('a')
      a.href = url
      a.download = 'Compeer Unterlagen.zip'
      a.click()
      URL.revokeObjectURL(url)
      toast.success(`${selected.size} Dokument${selected.size === 1 ? '' : 'e'} heruntergeladen`)
    } catch {
      toast.error('ZIP-Download fehlgeschlagen')
    } finally {
      setZipLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48" />
        <Skeleton className="h-32" />
        <Skeleton className="h-48" />
      </div>
    )
  }

  return (
    <div className="pb-24">
      <PageHeader
        title="Fristen"
        description="Dokumente mit Ablaufdatum — UBB, Bescheinigungen, Versicherungen"
        actions={
          <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
            <Settings2 className="h-4 w-4" /> Warn-E-Mails
          </Button>
        }
      />

      {/* Warn-Banner */}
      {stats && stats.warning_count > 0 && (
        <Card className={cn('mb-6 border', stats.expired_count > 0 ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50')}>
          <CardContent className="flex items-center gap-3 p-4">
            <AlertTriangle className={cn('h-5 w-5 shrink-0', stats.expired_count > 0 ? 'text-red-600' : 'text-amber-600')} />
            <p className="text-sm font-medium">
              {stats.warning_count} Dokument{stats.warning_count === 1 ? '' : 'e'} {stats.warning_count === 1 ? 'muss' : 'müssen'} erneuert werden
              {stats.expired_count > 0 && ` — davon ${stats.expired_count} bereits abgelaufen!`}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Gruppen */}
      <div className="space-y-6">
        {groups?.map(group => (
          <Card key={group.group_name}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-base">{group.group_name}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setNewCategoryGroup(group.group_name)}>
                <Plus className="h-4 w-4" /> Kategorie
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {group.categories.map(cat => {
                  const doc = cat.document
                  return (
                    <div
                      key={cat.id}
                      className={cn(
                        'flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4',
                        doc && !doc.snoozed && ROW_BG[doc.color]
                      )}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <input
                          type="checkbox"
                          className="h-4 w-4 shrink-0 rounded border-gray-300 accent-blue-600 disabled:opacity-30"
                          disabled={!doc}
                          checked={doc ? selected.has(doc.id) : false}
                          onChange={() => doc && toggleSelected(doc.id)}
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="truncate text-sm font-medium">{cat.name}</p>
                            <button
                              className="text-muted-foreground/50 hover:text-foreground"
                              onClick={() => setRenameTarget(cat)}
                              title="Umbenennen"
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                          </div>
                          {doc ? (
                            <a
                              href={fristenApi.getDownloadUrl(doc.id)}
                              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
                              title="Herunterladen"
                            >
                              <FileText className="h-3 w-3 shrink-0" />
                              <span className="truncate">{doc.original_file_name}</span>
                            </a>
                          ) : (
                            <p className="text-xs text-muted-foreground">Kein Dokument hochgeladen</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pl-7 sm:pl-0">
                        {doc && (
                          <>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatGermanDate(doc.expires_at)}
                            </span>
                            <StatusBadge doc={doc} />
                          </>
                        )}
                      </div>

                      <div className="flex items-center gap-1 pl-7 sm:pl-0">
                        <Button variant="outline" size="sm" onClick={() => setUploadTarget(cat)}>
                          <Upload className="h-3.5 w-3.5" />
                          <span className="hidden lg:inline">{doc ? 'Ersetzen' : 'Hochladen'}</span>
                        </Button>
                        {doc && <RequestedToggle doc={doc} />}
                        <Button variant="ghost" size="sm" onClick={() => setHistoryTarget(cat)} title="Historie">
                          <History className="h-3.5 w-3.5" />
                        </Button>
                        {!doc && <DeleteCategoryButton category={cat} />}
                      </div>
                    </div>
                  )
                })}
                {group.categories.length === 0 && (
                  <p className="px-4 py-6 text-center text-sm text-muted-foreground">Keine Kategorien</p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Sticky Auswahl-Leiste */}
      {selected.size > 0 && (
        <div className="fixed bottom-4 left-1/2 z-40 -translate-x-1/2">
          <Card className="shadow-lg">
            <CardContent className="flex items-center gap-3 p-3">
              <span className="text-sm font-medium whitespace-nowrap">
                {selected.size} ausgewählt
              </span>
              <Button size="sm" variant="outline" onClick={handleZipDownload} disabled={zipLoading}>
                {zipLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                ZIP
              </Button>
              <Button size="sm" onClick={() => setEmailOpen(true)}>
                <Mail className="h-4 w-4" /> E-Mail senden
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} title="Auswahl aufheben">
                <X className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Dialoge */}
      {uploadTarget && (
        <UploadDialog category={uploadTarget} onClose={() => setUploadTarget(null)} />
      )}
      {historyTarget && (
        <HistoryDialog category={historyTarget} onClose={() => setHistoryTarget(null)} />
      )}
      {renameTarget && (
        <RenameDialog category={renameTarget} onClose={() => setRenameTarget(null)} />
      )}
      {newCategoryGroup && (
        <NewCategoryDialog groupName={newCategoryGroup} onClose={() => setNewCategoryGroup(null)} />
      )}
      {emailOpen && (
        <EmailDialog
          documents={[...selected].map(id => allDocs.get(id)).filter(Boolean) as { doc: FristDocument; categoryName: string }[]}
          onClose={() => setEmailOpen(false)}
          onSent={() => { setEmailOpen(false); setSelected(new Set()) }}
        />
      )}
      {settingsOpen && <SettingsDialog onClose={() => setSettingsOpen(false)} />}
    </div>
  )
}

function RequestedToggle({ doc }: { doc: FristDocument }) {
  const markRequested = useMarkFristRequested()
  const isRequested = !!doc.requested_at

  const handleToggle = () => {
    markRequested.mutate(
      { documentId: doc.id, clear: isRequested },
      {
        onSuccess: () => toast.success(isRequested ? 'Anfrage-Markierung entfernt' : 'Als angefragt markiert — Warnungen 7 Tage pausiert'),
        onError: () => toast.error('Markierung fehlgeschlagen'),
      }
    )
  }

  return (
    <Button
      variant={isRequested ? 'secondary' : 'ghost'}
      size="sm"
      onClick={handleToggle}
      disabled={markRequested.isPending}
      title={isRequested ? 'Anfrage-Markierung entfernen' : 'Neues Dokument angefragt — Warnungen 7 Tage pausieren'}
    >
      <Clock className="h-3.5 w-3.5" />
      <span className="hidden xl:inline">{isRequested ? 'Angefragt ✓' : 'Angefragt'}</span>
    </Button>
  )
}

function DeleteCategoryButton({ category }: { category: FristCategory }) {
  const deleteCategory = useDeleteFristCategory()
  return (
    <Button
      variant="ghost"
      size="sm"
      title="Kategorie löschen"
      onClick={() => {
        if (!window.confirm(`Kategorie "${category.name}" wirklich löschen?`)) return
        deleteCategory.mutate(category.id, {
          onSuccess: () => toast.success('Kategorie gelöscht'),
          onError: (err: any) => toast.error(err?.response?.data?.error || 'Löschen fehlgeschlagen'),
        })
      }}
    >
      <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
    </Button>
  )
}

function UploadDialog({ category, onClose }: { category: FristCategory; onClose: () => void }) {
  const upload = useUploadFristDocument()
  const [file, setFile] = useState<File | null>(null)
  const [expiresAt, setExpiresAt] = useState('')

  const handleSubmit = () => {
    if (!file) { toast.error('Bitte eine Datei auswählen'); return }
    if (!expiresAt) { toast.error('Bitte ein Ablaufdatum angeben'); return }
    upload.mutate(
      { categoryId: category.id, file, expiresAt },
      {
        onSuccess: () => {
          toast.success(category.document ? 'Dokument ersetzt — altes Dokument in Historie' : 'Dokument hochgeladen')
          onClose()
        },
        onError: (err: any) => toast.error(err?.response?.data?.error || 'Upload fehlgeschlagen'),
      }
    )
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {category.document ? 'Dokument ersetzen' : 'Dokument hochladen'} — {category.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {category.document && (
            <p className="text-sm text-muted-foreground">
              Das aktuelle Dokument „{category.document.original_file_name}" wandert in die Historie.
            </p>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="frist-file">Datei (PDF, Bild, Office)</Label>
            <Input
              id="frist-file"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="frist-expires">Ablaufdatum</Label>
            <Input
              id="frist-expires"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSubmit} disabled={upload.isPending}>
            {upload.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Hochladen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function HistoryDialog({ category, onClose }: { category: FristCategory; onClose: () => void }) {
  const { data: history, isLoading } = useFristenHistory(category.id)

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Historie — {category.name}</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <Skeleton className="h-24" />
        ) : !history || history.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">Noch keine Dokumente</p>
        ) : (
          <div className="max-h-[50vh] space-y-2 overflow-y-auto">
            {history.map(entry => (
              <div key={entry.id} className="flex items-center justify-between gap-3 rounded-lg border p-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{entry.original_file_name}</p>
                  <p className="text-xs text-muted-foreground">
                    Ablauf: {formatGermanDate(entry.expires_at)}
                    {' · '}Hochgeladen: {formatGermanDate(entry.created_at)}
                    {entry.uploaded_by_name ? ` von ${entry.uploaded_by_name}` : ''}
                    {entry.replaced_at ? ` · Ersetzt: ${formatGermanDate(entry.replaced_at)}` : ''}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {entry.is_current === 1 && <Badge variant="secondary">Aktuell</Badge>}
                  <a href={fristenApi.getDownloadUrl(entry.id)} title="Herunterladen">
                    <Button variant="ghost" size="sm"><Download className="h-4 w-4" /></Button>
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function RenameDialog({ category, onClose }: { category: FristCategory; onClose: () => void }) {
  const rename = useRenameFristCategory()
  const [name, setName] = useState(category.name)

  const handleSubmit = () => {
    if (!name.trim()) { toast.error('Name ist erforderlich'); return }
    rename.mutate(
      { id: category.id, name: name.trim() },
      {
        onSuccess: () => { toast.success('Kategorie umbenannt'); onClose() },
        onError: (err: any) => toast.error(err?.response?.data?.error || 'Umbenennen fehlgeschlagen'),
      }
    )
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Kategorie umbenennen</DialogTitle></DialogHeader>
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSubmit()} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSubmit} disabled={rename.isPending}>Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function NewCategoryDialog({ groupName, onClose }: { groupName: string; onClose: () => void }) {
  const create = useCreateFristCategory()
  const [name, setName] = useState('')

  const handleSubmit = () => {
    if (!name.trim()) { toast.error('Name ist erforderlich'); return }
    create.mutate(
      { group_name: groupName, name: name.trim() },
      {
        onSuccess: () => { toast.success('Kategorie angelegt'); onClose() },
        onError: (err: any) => toast.error(err?.response?.data?.error || 'Anlegen fehlgeschlagen'),
      }
    )
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Neue Kategorie — {groupName}</DialogTitle></DialogHeader>
        <Input
          placeholder="Name der Kategorie"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSubmit} disabled={create.isPending}>Anlegen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EmailDialog({
  documents, onClose, onSent,
}: {
  documents: { doc: FristDocument; categoryName: string }[]
  onClose: () => void
  onSent: () => void
}) {
  const sendEmail = useSendFristEmail()
  const [to, setTo] = useState('')
  const [subject, setSubject] = useState('Compeer GmbH — Unterlagen')
  const [message, setMessage] = useState('Sehr geehrte Damen und Herren,\n\nanbei erhalten Sie die angeforderten Unterlagen.\n\nMit freundlichen Grüßen\nCompeer GmbH')

  const handleSubmit = () => {
    const parsed = sendFristEmailSchema.safeParse({ to, subject, message })
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message)
      return
    }
    sendEmail.mutate(
      { document_ids: documents.map(d => d.doc.id), to, subject, message },
      {
        onSuccess: () => { toast.success(`E-Mail mit ${documents.length} Anhang${documents.length === 1 ? '' : 'ängen'} gesendet`); onSent() },
        onError: (err: any) => toast.error(err?.response?.data?.error || 'E-Mail-Versand fehlgeschlagen'),
      }
    )
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Dokumente per E-Mail senden</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border bg-muted/40 p-3">
            <p className="mb-1 text-xs font-medium text-muted-foreground">Anhänge ({documents.length}):</p>
            <ul className="space-y-0.5">
              {documents.map(({ doc, categoryName }) => (
                <li key={doc.id} className="truncate text-sm">{categoryName} — {doc.original_file_name}</li>
              ))}
            </ul>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email-to">Empfänger (mehrere mit Komma trennen)</Label>
            <Input id="email-to" type="text" placeholder="empfaenger@firma.de" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email-subject">Betreff</Label>
            <Input id="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email-message">Nachricht</Label>
            <textarea
              id="email-message"
              className="flex min-h-[120px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSubmit} disabled={sendEmail.isPending}>
            {sendEmail.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Senden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SettingsDialog({ onClose }: { onClose: () => void }) {
  const { data: settings, isLoading } = useFristenSettings()
  const updateSettings = useUpdateFristenSettings()
  const [recipients, setRecipients] = useState<string | null>(null)

  const value = recipients ?? settings?.warn_recipients ?? ''

  const handleSubmit = () => {
    updateSettings.mutate(
      { warn_recipients: value },
      {
        onSuccess: () => { toast.success('Empfänger gespeichert'); onClose() },
        onError: (err: any) => toast.error(err?.response?.data?.error || 'Speichern fehlgeschlagen'),
      }
    )
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5" /> Warn-E-Mails
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            An diese Adresse(n) werden die Ablauf-Warnungen geschickt (30/14/7 Tage vor Ablauf,
            bei Ablauf sowie die Erinnerung nach „Angefragt"). Mehrere Adressen mit Komma trennen.
            Ohne Empfänger werden keine Warn-E-Mails versendet.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="warn-recipients">Empfänger</Label>
            <Input
              id="warn-recipients"
              type="text"
              placeholder="buero@compeer-gmbh.de"
              value={value}
              disabled={isLoading}
              onChange={(e) => setRecipients(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSubmit} disabled={updateSettings.isPending || isLoading}>Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
